import { AddressModel } from "shared/models/address-model";
import { BranchModel } from "states/branch/branch.models";
import { CustomerModel } from "states/customer/customer.models";
import {
  ProductModel,
  WholesalePriceType,
} from "states/product/product.models";
import { StaffModel } from "states/staff/staff.models";
import { WareHouseModel } from "states/ware-house/ware-house.models";

export interface OrderModel {
  key: string;
  id: string;
  orderNumber: string;
  subTotal: number;
  discountAmount: number;
  taxes: number;
  taxRate: number;
  paymentDone: boolean;
  shippingFee: number;
  totalAmount: number;
  paidAmount: number;
  note: string;
  tipAmount: number;
  couponCode?: string;
  cartPromotionName?: string;
  status: string;
  type: string;
  staff?: StaffModel;
  processingStaff?: StaffModel;
  customer: CustomerModel;
  branch: BranchModel;
  wareHouse: WareHouseModel;
  orderItems: OrderItemModel[];
  orderPayments: OrderPaymentModel[];
  orderProcessings: OrderProcessingModel[];
  orderPackingLists: OrderPackingListModel[];
  creationTime: Date;
  lastModificationTime: Date;
  requestPickupTime: Date;
}

export interface OrderItemModel {
  id: string;
  product: ProductModel;
  priceTotal: number;
  quantity: number;
  shippedQuantity: number;
  listedPrice: number;
  modifiers: OrderItemModel[];
}

export interface SearchOrderRequestModel {
  keyword?: string;
  orderStatus?: string[];
  paymentStatus?: string[];
  staffs?: number[];
  fromDate?: Date;
  toDate?: Date;
  branchId?: number;
  page: number;
  pageSize: number;
}

export interface OrderPaymentModel {
  id: string;
  amount: number;
  orderId: string;
  referenceId: string;
  ipAddress: string;
  paymentNote: string;
  paymentMethod: string;
  extensionFields: string;
  creationTime: Date;
  paymentGatewayCode: string;
  paymentStatus: string;
}

export interface OrderProcessingModel {
  id: string;
  creator: string;
  description: string;
  staff?: StaffModel;
  creationTime: Date;
  ipAddress: string;
}

export interface OrderPackingListModel {
  id: string;
  orderId: string;
  shippingAddress?: AddressModel;
  type: string;
  status: string;
  notes?: string;
  requestedPickupTime?: Date;
  confirmedPickupTime?: Date;
  collectionAmount: number;
  billOfLadingCode: string;
  shippingCompanyCode?: string;
  orderPackingListItems: OrderPackingListItemModel[];
}

export interface OrderPackingListItemModel {
  id: string;
  orderItemId: string;
  product: ProductModel;
  shippedQuantity: number;
}

export function calculateSubTotal(order: OrderModel) {
  order.subTotal = order.orderItems
    .map((m) => getOrderItemPriceWithModifierTotal(m))
    .reduce((a, b) => a + b, 0);
  calculateTotal(order);
}
export function calculateTotal(order: OrderModel) {
  order.totalAmount =
    order.subTotal + order.taxes + order.shippingFee - order.discountAmount;
}

export function getOrderItemPriceWithModifierTotal(item: OrderItemModel) {
  return (
    item.priceTotal +
    item.modifiers.map((mod) => mod.priceTotal).reduce((a, b) => a + b, 0)
  );
}

export function getProductPriceTotal(
  product: ProductModel,
  quantity: number = 1,
  listedPriceSubtotal: number = 0
) {
  if (listedPriceSubtotal > 0 && !product.isModifier) {
    const priceBySubTotalItem = product.priceBySubTotal?.items.find(
      (m) =>
        m.subTotalFrom <= listedPriceSubtotal &&
        m.subTotalTo >= listedPriceSubtotal
    );
    if (priceBySubTotalItem) {
      return priceBySubTotalItem.sellingPrice * quantity;
    }
  }

  if (quantity > 1 && !product.isModifier) {
    const wholesaleItem = product.wholesale?.items.find(
      (m) => m.quantityFrom <= quantity && m.quantityTo >= quantity
    );
    if (wholesaleItem) {
      return wholesaleItem.priceType === WholesalePriceType.Total
        ? wholesaleItem.sellingPrice
        : wholesaleItem.sellingPrice * quantity;
    }
  }

  return product.sellingPrice * quantity;
}

export function getOrderListedPriceSubTotal(order: OrderModel) {
  return order.orderItems
    .map(
      (m) =>
        m.listedPrice * m.quantity +
        m.modifiers.map((mod) => mod.priceTotal).reduce((a, b) => a + b, 0) *
          m.quantity
    )
    .reduce((a, b) => a + b, 0);
}

export function processOrderItemPrice(order: OrderModel) {
  order.orderItems.forEach((item) => {
    item.listedPrice = item.product.price ?? 0;
    item.modifiers.forEach((mod) => {
      mod.listedPrice = mod.product.price ?? 0;
    });
  });
  order.orderItems.forEach((item) => {
    item.priceTotal = getProductPriceTotal(
      item.product,
      item.quantity,
      getOrderListedPriceSubTotal(order)
    );
    item.modifiers.forEach((mod) => {
      mod.priceTotal = getProductPriceTotal(mod.product);
    });
  });
}

export abstract class OrderConst {
  public static status = {
    pending: "Pending",
    onOrder: "OnOrder",
    shipping: "Shipping",
    received: "Received",
    completed: "Completed",
    cancel: "Cancel",
    returning: "Returning",
    returned: "Returned",
  };
  public static orderType = {
    online: "Online",
    directSales: "DirectSales",
  };
}

export abstract class OrderShippingConst {
  public static status = {
    pending: "Pending",
    shipping: "Shipping",
    received: "Received",
    cancel: "Cancel",
    returning: "Returning",
    returned: "Returned",
  };
  public static shippingType = {
    delivery: "Delivery",
    pickup: "Pickup",
  };
  public static ShippingCompany = {
    ghtk: "Ghtk",
    viettelPost: "ViettelPost",
    ghn: "Ghn",
    other: "Other",
  };
}

export abstract class OrderPaymentConst {
  public static method = {
    cod: "Cod",
    card: "Card",
    cash: "Cash",
    transfer: "Transfer",
  };
  public static status = {
    pending: "Pending",
    done: "Done",
  };
}
